import { useReducedMotion } from 'framer-motion'
import { type CSSProperties, useEffect, useRef, useState } from 'react'
import { cn } from '~/shared/lib/utils'
import { barGeometry } from './stacked-bars-data'
import './stacked-bars.css'

const SPARK_GAP = 1
const STAGGER_MS = 240

/** A tiny one-color bar sparkline in the current text color; decorative, without tooltip. */
export function SparkBars({
  values,
  width = 64,
  height = 20,
  className,
}: {
  values: readonly number[]
  width?: number
  height?: number
  className?: string
}) {
  const reduced = useReducedMotion() ?? false
  const root = useRef<HTMLDivElement | null>(null)
  const [grown, setGrown] = useState(false)

  useEffect(() => {
    if (grown) return
    if (reduced || typeof IntersectionObserver === 'undefined') {
      setGrown(true)
      return
    }
    const el = root.current
    if (!el) return
    const observer = new IntersectionObserver(([entry]) => {
      if (entry?.isIntersecting) {
        setGrown(true)
        observer.disconnect()
      }
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [grown, reduced])

  const n = values.length
  const { gap, barWidth } = barGeometry(width, n, SPARK_GAP)
  const max = Math.max(1, ...values)
  const radius = Math.min(1.5, barWidth / 2)

  return (
    <div
      ref={root}
      aria-hidden
      className={cn('stb-spark', className)}
      style={
        {
          width,
          height,
          '--stb-r': `${radius}px`,
          '--stb-step': `${n > 0 ? STAGGER_MS / n : 0}ms`,
        } as CSSProperties
      }
    >
      {values.map((value, index) => (
        <span
          // biome-ignore lint/suspicious/noArrayIndexKey: bars are positional, one per day.
          key={index}
          className="stb-bar"
          style={
            {
              left: index * (barWidth + gap),
              width: barWidth,
              height: grown ? Math.max(value > 0 ? 1.5 : 1, (value / max) * height) : 0,
              opacity: value > 0 ? 1 : 0.35,
              '--i': index,
            } as CSSProperties
          }
        />
      ))}
    </div>
  )
}
